import {
  Close as CloseIcon,
  Dashboard as DashboardIcon,
  Edit as EditIcon,
  OpenInFull as OpenInFullIcon,
} from '@mui/icons-material';
import {
  Box,
  Button,
  DialogActions,
  IconButton,
  Stack,
  Typography,
} from '@mui/material';
import { useContext } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { AuthDataProvider } from 'src/components/authProvider';
import { FullGcfComponentSection } from 'src/components/gcfComponentSection';
import { PreviewModal } from 'src/components/modal';
import { Progress } from 'src/components/progress';
import { ScaleContainer } from 'src/components/scaleContainer/ScaleContainer';
import { PATHS } from 'src/constants/paths';
import { useModal } from 'src/hooks';
import { useGetComponentsWithIncludes } from 'src/hooks/api/components/useGetComponents';
import { useGetAllComponentTags } from 'src/hooks/api/componentTags/useGetAllComponentTags';
import { ComponentTypes, IStatus } from 'src/interfaces';
import {
  generateAssemblyProductFilters,
  getComponentDisplayName,
  lang,
  useUserRole,
} from 'src/utils';
import { getSectionPreviewData } from 'src/utils/gcf-helper';
import { enterEditComponentFromInfoEvent } from 'src/utils/google-tag-manager';

import { ComponentPreviewSidebarProps } from './ComponentPreviewSidebar.interface';
import {
  CardButtonContainer,
  CardInfoDescription,
  CardInfoTitle,
  ComponentContainer,
  ComponentTitleContainer,
  Container,
  StyledChipContainer,
  StyledDynamicTooltipChip,
} from './ComponentPreviewSidebar.styled';

export const ComponentPreviewSidebar = (
  props: ComponentPreviewSidebarProps,
) => {
  const { isViewOnly } = useUserRole();
  const navigate = useNavigate();
  const { userRole } = useContext(AuthDataProvider);

  const componentInfoModal = useModal();

  const { data: selectedChipTags = [] } = useGetAllComponentTags(
    userRole,
    [`id.list=${(props.selectedCard?.component_tag_id ?? []).join('|')}`],
    Boolean((props.selectedCard?.component_tag_id?.length ?? 0) > 0),
  );

  const compId = props.selectedCard?.id;

  const filters = compId ? [`id.list=${compId}`] : [];

  const { data: componentData } = useGetComponentsWithIncludes(
    filters,
    ['assets'],
    {
      enabled: !!compId,
    },
  );

  const { productFilters, productVariationFilters } =
    generateAssemblyProductFilters([], componentData ? [componentData[0]] : []);

  if (!props?.selectedCard) return null;

  return (
    <>
      <Container spacing={4}>
        {!props?.hideSidebarPreviewButton && (
          <ComponentTitleContainer
            $isReplacementComponent={props?.isReplacementComponent}>
            {!props?.isReplacementComponent && (
              <Button
                variant="outlined"
                onClick={componentInfoModal.onOpenModal}
                startIcon={<OpenInFullIcon />}>
                {lang.expand}
              </Button>
            )}
            <IconButton aria-label="close" onClick={props.onClose}>
              <CloseIcon />
            </IconButton>
          </ComponentTitleContainer>
        )}
        <ComponentContainer>
          {props.isLoadingSidebarPreview ? (
            <Progress />
          ) : (
            <>
              {!!props.hasSidebarPreviewLoaded && (
                <ScaleContainer
                  containerStyle={{
                    height: '100%',
                    minWidth: '100%',
                  }}>
                  <FullGcfComponentSection
                    assemblyComponents={[
                      {
                        component: {
                          type: props?.selectedCard.component_type?.name,
                          config: props?.selectedCard?.config,
                          name: props?.selectedCard.name,
                          width: props?.selectedCard.width,
                          height: props?.selectedCard.height,
                          theme: props.selectedCard.theme?.name,
                        },
                      },
                    ]}
                    height={props?.selectedCard?.height || 0}
                    width={props?.selectedCard?.width || 0}
                    previewData={getSectionPreviewData(
                      props?.selectedCard.component_type
                        ?.name as ComponentTypes,
                    )}
                    containerStyle={{
                      borderWidth: '1px',
                      borderStyle: 'solid',
                      borderColor: 'brand.darkGrey',
                    }}
                    overrideQuerySetting={{
                      staleTime: Infinity,
                    }}
                    productFilters={productFilters}
                    productVariationFilters={productVariationFilters}
                    skipProductsApiOnEmptyFilters={
                      !(productFilters.length || productVariationFilters.length)
                    }
                  />
                </ScaleContainer>
              )}
            </>
          )}
        </ComponentContainer>
        <CardInfoTitle>{props?.selectedCard?.name}</CardInfoTitle>
        <CardInfoDescription>
          <Stack>
            <Typography variant="subtitle1">{lang.componentType}</Typography>

            <Typography fontWeight={200}>
              {getComponentDisplayName(
                props?.selectedCard?.component_type?.name as ComponentTypes,
              )}
            </Typography>
          </Stack>
          <Stack>
            <Typography variant="subtitle1">{lang.size}</Typography>

            <Typography fontWeight={200}>
              {lang.previewModal.sizeDescription({
                width: props.selectedCard?.width,
                height: props.selectedCard?.height,
              })}
            </Typography>
          </Stack>
        </CardInfoDescription>
        {selectedChipTags.length > 0 && (
          <CardInfoDescription>
            <Stack>
              <Typography variant="subtitle1">{lang.tags}</Typography>
              <Typography>
                <Box overflow={'auto'} maxHeight={200}>
                  {selectedChipTags.map((tag) => (
                    <StyledChipContainer>
                      <StyledDynamicTooltipChip
                        key={tag.id}
                        chipName={tag?.name}
                        tooltipTitle={tag?.name}
                        tooltipWidth={500}
                        tooltipActivationWidth={252}
                      />
                    </StyledChipContainer>
                  ))}
                </Box>
              </Typography>
            </Stack>
          </CardInfoDescription>
        )}
        {!props?.isReplacementComponent && (
          <CardButtonContainer>
            <Button
              fullWidth
              variant="outlined"
              component={Link}
              to={`details/${props.selectedCard.id}`}>
              {lang.viewDetails}
            </Button>
            {!isViewOnly && (
              <Button
                fullWidth
                endIcon={<EditIcon />}
                disabled={
                  'status' in props.selectedCard &&
                  props.selectedCard.status === IStatus.READY
                }
                component={Link}
                to={`edit/${props.selectedCard.id}`}
                {...('component_type_id' in props.selectedCard && {
                  onClick: () =>
                    enterEditComponentFromInfoEvent(
                      props?.selectedCard?.id ?? 0,
                    ),
                })}>
                {lang.edit}
              </Button>
            )}
          </CardButtonContainer>
        )}
        {props?.isReplacementComponent && (
          <DialogActions sx={{ mt: 'auto' }}>
            <Button
              disabled={
                !props.selectedCard ||
                !props.componentId ||
                !props.replacementId
              }
              onClick={() => {
                if (props.componentId && props.replacementId) {
                  navigate(
                    PATHS.COMPONENT_BULK_REPLACE(
                      props.componentId,
                      props.replacementId,
                    ),
                  );
                }
              }}
              startIcon={<DashboardIcon />}
              fullWidth>
              {lang.selectComponent}
            </Button>
          </DialogActions>
        )}
      </Container>
      {componentInfoModal.open && props.selectedCard && (
        <PreviewModal
          open={componentInfoModal.open}
          onClose={componentInfoModal.onCloseModal}
          type="component"
          content={props.selectedCard}
          productFilters={productFilters}
          productVariationFilters={productVariationFilters}
          skipProductsApiOnEmptyFilters={
            !(productFilters.length || productVariationFilters.length)
          }
        />
      )}
    </>
  );
};
